import { createContext, type Dispatch, type SetStateAction } from "react";
import {
    BulkUpdateTransactionsRequestInput,
    TransactionListItem,
} from "@common/schemas/transaction.schema";
import { UpdateTransactionDto } from "@common/dto/transactions/update-transaction.dto";
import { Transaction } from "@common/types/transaction";
import {
    RuleGroupType,
    TransactionRuleConditionDefinition,
} from "@common/types/transactionRule";
import { TransactionView } from "@common/types/views/transactionViews";
import { noop, noopAsync } from "@common/helpers/function";
import type { LoadMoreTableState } from "@hooks/useLoadMoreTableState";
import { TransactionsFilters } from "./filters/lib";
import { TransactionSortValue, UseSort } from "./useSort";
import { UseTransactionsQueryParam } from "./useTransactionsQuery";
import { BulkUpdateDetails } from "./TransactionsBulkActions/lib";
import { SelectedTransactionBase } from "./TransactionsBulkActions/transactionsBulkActionsContext";
import { createDefaultTransactionView } from "./views/DefaultTransactionView";

export interface MatchingRuleData {
    ruleId: string;
    conditions: TransactionRuleConditionDefinition[];
    groupType: RuleGroupType;
    // AI-rule cards open the drawer already in "currently matching" mode so its
    // default rows/total match the card's live count (PRO-9305).
    defaultToMatchingMode?: boolean;
}

export interface BulkUpdateResult {
    updatedTransactions: Transaction[];
    details: BulkUpdateDetails;
    transactionsBeforeUpdate: Transaction[];
    canonicalNormalizedDescriptionId?: string | null;
}

/** Load-more state for the list; the table supplies its own error copy. */
export type TransactionsLoadMore = LoadMoreTableState & {
    /** The id snapshot stopped at its cap, so more rows match than load. */
    isTruncated: boolean;
};

export interface PaginatedTransactionsDataContextValue {
    /** Set when the list loads more on scroll instead of paging. */
    loadMore?: TransactionsLoadMore;
    currentPage: number;
    totalPages: number;
    transactionCount: number;
    isTransactionsFetching: boolean;
    currentSort: UseSort["currentSort"];
    filters: UseTransactionsQueryParam["filters"];
    transactions?: TransactionListItem[];
    shownTransactionId?: number;
    currentView: TransactionView;
    // Id of the view a deep link landed on, so the toolbar can show it as the
    // selected base view instead of whatever was cached.
    initialBaseViewId?: string;
    updateDetails: BulkUpdateDetails | null;
    isMatchingRuleMode: boolean;
    matchingRuleTotal?: number;
    disableSimilarTransactionsUpdate?: boolean;
    isRulesSuggestionAvailable: boolean;
    isRulesSuggestionEnabled: boolean;
}

const defaultTransactionView = createDefaultTransactionView({
    entityIds: [],
    hasAnyClass: true,
    isGlFirst: false,
});

const defaultDataValue: PaginatedTransactionsDataContextValue = {
    currentPage: 0,
    totalPages: 0,
    transactionCount: 0,
    isTransactionsFetching: false,
    currentSort: TransactionSortValue.NONE,
    filters: { entityIds: [] },
    currentView: {
        type: defaultTransactionView.type,
        columns: defaultTransactionView.columns,
        filters: defaultTransactionView.filters,
        options: defaultTransactionView.options,
    },
    updateDetails: null,
    isMatchingRuleMode: false,
    matchingRuleTotal: undefined,
    isRulesSuggestionAvailable: false,
    isRulesSuggestionEnabled: false,
};

export const PaginatedTransactionsDataContext =
    createContext<PaginatedTransactionsDataContextValue>(defaultDataValue);

export interface PaginatedTransactionsActionsContextValue {
    setPage: Dispatch<SetStateAction<number>>;
    handleSortChange: UseSort["handleSortChange"];
    setCurrentFilters: (filters: TransactionsFilters) => void;
    saveTransaction: (
        transaction: TransactionListItem,
        payload: UpdateTransactionDto,
        displayedChangedEntityName?: string,
    ) => Promise<TransactionListItem | undefined>;
    upsertTransaction(transaction: TransactionListItem): void;
    updateMany(transactions: TransactionListItem[]): void;
    setShownTransactionId: (t?: number) => void;
    setCurrentView: (
        view: TransactionView | ((prev: TransactionView) => TransactionView),
    ) => void;
    doUpdate: (
        details: BulkUpdateDetails,
        payloads: BulkUpdateTransactionsRequestInput[],
        onUpdated?: (
            result: TransactionListItem[],
            details: BulkUpdateDetails,
            previousTransactions: Record<number, Transaction>,
        ) => void,
    ) => Promise<BulkUpdateResult>;
    undo: (params: {
        previousTransactions: Record<number, SelectedTransactionBase>;
        updateDetailsForUndo: BulkUpdateDetails;
    }) => unknown;
    setUpdateDetails: Dispatch<SetStateAction<BulkUpdateDetails | null>>;
    setIsMatchingRuleMode: (mode: boolean) => void;
    setRulesSuggestionEnabled: (isEnabled: boolean) => void;
}

const defaultActionsValue: PaginatedTransactionsActionsContextValue = {
    setPage: () => {},
    handleSortChange: () => {},
    setCurrentFilters: () => {},
    saveTransaction: async (t) => t,
    upsertTransaction: noop,
    updateMany: noop,
    setShownTransactionId: noop,
    setCurrentView: noop,
    doUpdate: noopAsync,
    undo: noop,
    setUpdateDetails: noop,
    setIsMatchingRuleMode: noop,
    setRulesSuggestionEnabled: noop,
};

export const PaginatedTransactionsActionsContext =
    createContext<PaginatedTransactionsActionsContextValue>(
        defaultActionsValue,
    );
